<!-- 
  @component
  UI is logic component that updates the state of the UI layout store.
  
 -->
<script context="module" lang="ts">
  import { derived, type Readable } from "svelte/store";
  import { type IconType } from "./Icon.svelte";

  type AllArgsAction = (...args: any[]) => string | symbol;
  type Action = AllArgsAction | ((() => string) & (() => string) & (() => string));
  type Btn = { icon: IconType; action: Action }
  type TopbarUI = {
    btn1: Btn;
    input: boolean;
    text?: Readable<string>; 
    btn2: Btn | false;
  }

  type MainUI = {
    top: boolean;
    floor: boolean;
    aside: boolean;
    bottom: boolean;
  }
  
  export type UI = {
    topbar: TopbarUI;
    main: Partial<MainUI>;
  }
</script>

<script lang="ts">
  import { getContext, setContext } from "svelte";
  import { VisioMapApp } from "./vg/visiomap";

  const app = getContext<VisioMapApp>('app');
  const { is_desktop, is_mobile, state, src_poi, dst_poi } = app;

  const map_view_topbar: TopbarUI = {
    btn1: { icon: 'search', action: app.state.showListing },
    input: true,
    btn2: { icon: 'info', action: app.state.showInfo }
  }

  const embedded_view: UI = {
    topbar: { btn1: { icon: 'search', action: app.state.showListing }, input: false, btn2: false },
    main : {}
  }

  let map_view: UI;
  let list_view: UI;
  let detail_view: UI;
  let route_setup_view: UI;
  let navigation_view: UI;
  let info_view: UI;

  $: {
    map_view = {
      topbar: map_view_topbar,
      main : {
        top: $is_mobile,
        floor: true,
        aside: $is_desktop,
      }
    }
  }

  $: {
    list_view = {
      topbar: {
        btn1: { icon: 'back', action: app.state.back },
        input: true,
        btn2: { icon: 'close', action: app.state.clearSearch }
      },
      main: {
        floor: $is_desktop,
        aside: true,
      }
    }
  }

  $: { 
    detail_view = {
      topbar: {
        btn1: { icon: 'back', action: app.state.back },
        input: true,
        btn2: { icon: 'close', action: app.state.clearSearch }
      },
      main: {
        floor: $is_desktop,
        aside: true,
      }
    }
  }
  
  $: { 
    route_setup_view = {
      topbar: {
        btn1: { icon: 'back', action: app.state.back },
        input: true,
        btn2: { icon: 'close', action: app.state.clearSetup }
      },
      main: {
        floor: $is_desktop,
        aside: true,
      }
    }
  }

  const navigation_title = derived([src_poi, dst_poi], ([src, dst]) => {
    return src && dst
      ? `<span>${src.label}</span> → <span>${dst.label}</span>`
      : '';
  });
  $: {
    navigation_view = {
      topbar: {
        btn1: { icon: 'back', action: app.state.back },
        input: false,
        text: navigation_title,
        btn2: { icon: 'close', action: app.state.clear }
      },
      main: {
        top: $is_mobile,
        floor: $is_desktop,
        aside: $is_desktop,
        bottom: $is_mobile,
      }
    }
  }

  $: {
    info_view = {
      topbar: {
        btn1: { icon: 'back', action: app.state.back },
        input: false,
        btn2: false
      },
      main: {
        floor: $is_desktop,
        aside: true,
      }
    }
  }


  const ui_layout = derived(state, $state => {
    switch ($state) {
      case 'embedded_view': return embedded_view; 
      case 'map_view': return map_view;
      case 'list_view': return list_view;
      case 'detail_view': return detail_view;
      case 'navigation_view': return navigation_view;
      case 'route_setup_view': return route_setup_view;
      case 'info_view': return info_view;
    }
  })

  setContext('ui_layout', ui_layout);
</script>

<slot />
